
import React, { useState } from 'react';
import { Button } from '../../../components/base/Button';

export const ContactSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSubmit = new FormData();
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4tai59t85rh3e48p38g', {
        method: 'POST',
        body: formDataToSubmit
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section 
      id="contact" 
      className="py-20 lg:py-32"
      style={{
        background: 'linear-gradient(180deg, #E8EEF2 0%, #ffffff 100%)'
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left Column - Contact Info */}
          <div className="space-y-10">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] mb-8">
                Get In Touch
              </h2>
            </div>

            <div className="space-y-10">
              {/* Email */}
              <div className="space-y-2">
                <div className="text-xs uppercase text-gray-600 font-medium tracking-wider">
                  EMAIL
                </div>
                <a 
                  href="mailto:information@balestradoocacak.com"
                  className="text-xl text-[#1A4D7C] hover:text-[#00D4FF] transition-colors duration-300 font-medium"
                >
                  information@balestradoocacak.com
                </a>
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <div className="text-xs uppercase text-gray-600 font-medium tracking-wider">
                  PHONE
                </div>
                <a 
                  href="tel:+19855318157"
                  className="text-xl text-[#1A4D7C] hover:text-[#00D4FF] transition-colors duration-300 font-medium"
                >
                  +1 985 531 8157
                </a>
              </div>

              {/* Address */}
              <div className="space-y-3">
                <div className="text-xs uppercase text-gray-600 font-medium tracking-wider">
                  ADDRESS
                </div>
                <div className="space-y-2">
                  <div className="text-lg text-[#0A1628] font-medium">
                    Stara Pruga 13
                  </div>
                  <div className="text-gray-600">
                    Preljina, 525305
                  </div>
                  <div className="text-gray-600">
                    Serbia
                  </div>
                </div>
              </div>
            </div>

            {/* Company Visual */}
            <div className="mt-12 relative">
              <img 
                src="https://public.readdy.ai/ai/img_res/fee8672d9da6f13d8a5a326824426f3d.jpg"
                alt="BALESTRA Office"
                className="w-full h-48 object-cover object-center rounded-2xl shadow-lg"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0A1628]/20 to-transparent rounded-2xl"></div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div>
            <div className="bg-white rounded-3xl shadow-xl p-8 lg:p-12">
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
                {/* Name Field */}
                <div className="space-y-2">
                  <label htmlFor="name" className="text-sm text-gray-600 font-medium">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full h-12 bg-transparent border-0 border-b-2 border-gray-200 focus:border-[#00D4FF] transition-colors duration-300 text-gray-900 placeholder-gray-500 outline-none"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm text-gray-600 font-medium">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full h-12 bg-transparent border-0 border-b-2 border-gray-200 focus:border-[#00D4FF] transition-colors duration-300 text-gray-900 placeholder-gray-500 outline-none"
                    placeholder="your.email@example.com"
                  />
                </div>

                {/* Message Field */}
                <div className="space-y-2">
                  <label htmlFor="message" className="text-sm text-gray-600 font-medium">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full bg-transparent border-0 border-b-2 border-gray-200 focus:border-[#00D4FF] transition-colors duration-300 text-gray-900 placeholder-gray-500 outline-none resize-none"
                    placeholder="Tell us about your VPN security needs..."
                  ></textarea>
                  <div className="text-xs text-gray-500 text-right">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit"
                    variant="primary" 
                    size="lg"
                    className="w-full"
                    disabled={isSubmitting || formData.message.length > 500}
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending Message...
                      </>
                    ) : (
                      <>
                        Send Message
                        <i className="ri-arrow-right-line"></i>
                      </>
                    )}
                  </Button>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-check-line mr-2"></i>
                      Message sent successfully! We'll get back to you soon.
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line mr-2"></i>
                      Failed to send message. Please try again or contact us directly.
                    </div>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
